import { CdkConnectedOverlay, OverlayModule } from '@angular/cdk/overlay';
import { Component, ElementRef, OnDestroy, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { DateInputMaskDirective, DatepickerComponent } from 'ngx-lib';

@Component({
    selector: 'app-custom-cell-datepicker',
    standalone: true,
    imports: [DateInputMaskDirective, DatepickerComponent, OverlayModule],
    templateUrl: './custom-cell-datepicker.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './custom-cell-datepicker.component.scss'
})
export class CustomCellDatepickerComponent implements ICellEditorAngularComp, OnDestroy {
    @ViewChild('dateInput') dateInput!: ElementRef<HTMLInputElement>;
    @ViewChild('container', { static: false }) container!: ElementRef<HTMLElement>;

    selectedDateTime = signal<DateTime | undefined>(undefined);
    keyboardDate = '';
    open = signal<boolean>(false);

    // NOTE: This format is intentionally hardcoded. We are aware that it ignores
    // the browser's locale date order (e.g. dd/MM/yyyy for non-US users).
    // We are keeping it as-is for now since our user base primarily uses MM/dd/yyyy,
    // and making this locale-aware would require changes across the app
    // that would need broader testing.
    readonly dateFormat = 'MM/dd/yyyy';

    private currentDateTime: DateTime | undefined = undefined;
    private params!: ICellEditorParams;

    agInit(params: ICellEditorParams): void {
        this.params = params;

        this.params.api.addEventListener('bodyScroll', this.bodyScrollListener);

        if (params.value instanceof Date) {
            const date = DateTime.fromJSDate(params.value);
            this.selectedDateTime.set(date);
            this.currentDateTime = date;
            this.keyboardDate = date.toFormat(this.dateFormat);
        }
    }

    afterGuiAttached(): void {
        // Open after AG Grid placed the editor, so CDK measures the real origin position.
        this.open.set(true);
    }

    ngOnDestroy(): void {
        if (this.params?.api) {
            this.params.api.removeEventListener('bodyScroll', this.bodyScrollListener);
        }
    }

    getValue(): Date | null {
        const date = this.currentDateTime;
        return date ? date.toJSDate() : null;
    }

    isPopup(): boolean {
        return true;
    }

    onOverlayAttach(overlay: CdkConnectedOverlay): void {
        // Reposition once the datepicker has its real height so CDK can flip if needed.
        setTimeout(() => {
            overlay.overlayRef.updatePosition();
            this.dateInput?.nativeElement.focus();
        });
    }

    onKeyboardDateBlur(isValidDate: boolean): void {
        if (!this.keyboardDate) {
            this.clearDate();
            return;
        }

        if (!isValidDate) {
            this.setKeyboardDate();
        }
    }

    onKeyboardDateChange(dateValue: string): void {
        this.keyboardDate = dateValue;
    }

    onDatepickerChanged(): void {
        const date = this.selectedDateTime();
        this.currentDateTime = date;
        this.keyboardDate = date ? date.toFormat(this.dateFormat) : '';
        this.open.set(false);
        this.params.api.stopEditing();
    }

    onEnterPressed(event: Event): void {
        event.preventDefault();

        if (!this.keyboardDate) {
            this.clearDate();
            this.open.set(false);
            this.params.api.stopEditing();
            return;
        }

        const inputDateTime = DateTime.fromFormat(this.keyboardDate, this.dateFormat);

        if (inputDateTime.isValid) {
            this.applyDateFromInput(inputDateTime);
            this.currentDateTime = this.selectedDateTime();
            this.open.set(false);
            this.params.api.stopEditing();
        }

        this.setKeyboardDate();
    }

    onTabPressed(event: Event): void {
        const e = event as KeyboardEvent;

        e.preventDefault();
        e.stopPropagation();

        if (e.shiftKey) {
            this.dateInput?.nativeElement.focus();
            return;
        }

        setTimeout(() => {
            this.container?.nativeElement?.focus();
        });
    }

    private setKeyboardDate(): void {
        const dt = this.selectedDateTime();
        this.keyboardDate = dt ? dt.toFormat(this.dateFormat) : '';
    }

    private applyDateFromInput(inputDateTime: DateTime): void {
        const current = this.selectedDateTime();
        const updatedDateTime = current
            ? current.set({ year: inputDateTime.year, month: inputDateTime.month, day: inputDateTime.day })
            : inputDateTime;
        this.selectedDateTime.set(updatedDateTime);
    }

    private readonly bodyScrollListener = () => this.onBodyScroll();

    private onBodyScroll(): void {
        this.open.set(false);
        this.params.api.stopEditing(true);
    }

    private clearDate(): void {
        this.selectedDateTime.set(undefined);
        this.currentDateTime = undefined;
        this.keyboardDate = '';
    }
}
